import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon, Download } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useState } from 'react';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';

interface BillingFiltersProps {
  dateRange?: { from?: Date; to?: Date };
  onDateRangeChange: (range?: { from?: Date; to?: Date }) => void;
  clientTier: string;
  onClientTierChange: (tier: string) => void;
  onExport: () => void;
  isExporting?: boolean;
  className?: string;
}

export default function BillingFilters({
  dateRange,
  onDateRangeChange,
  clientTier,
  onClientTierChange,
  onExport,
  isExporting = false,
  className
}: BillingFiltersProps) {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const presetRanges = [
    {
      label: 'Last 7 days',
      range: {
        from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        to: new Date()
      }
    },
    {
      label: 'Last 30 days',
      range: {
        from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        to: new Date()
      }
    },
    {
      label: 'Last 90 days',
      range: {
        from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
        to: new Date()
      }
    },
    {
      label: 'Year to date',
      range: {
        from: new Date(new Date().getFullYear(), 0, 1),
        to: new Date()
      }
    }
  ];

  return (
    <div className={cn("flex flex-col sm:flex-row gap-4", className)}>
      <div className="flex flex-1 gap-4">
        {/* Date Range Picker */}
        <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !dateRange && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "LLL dd, y")} -{" "}
                    {format(dateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(dateRange.from, "LLL dd, y")
                )
              ) : (
                <span>Pick a date range</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="p-2 space-y-1 border-b">
              {presetRanges.map((preset) => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    onDateRangeChange(preset.range);
                    setIsDatePickerOpen(false);
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <Calendar
              mode="range"
              selected={{
                from: dateRange?.from,
                to: dateRange?.to
              } as DateRange}
              onSelect={(range) => {
                onDateRangeChange(range as DateRange | undefined);
                if (range && 'from' in range && 'to' in range && range.from && range.to) {
                  setIsDatePickerOpen(false);
                }
              }}
              numberOfMonths={2}
              className="rounded-md border"
            />
          </PopoverContent>
        </Popover>

        {/* Client Tier Filter */}
        <Select value={clientTier} onValueChange={onClientTierChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Export Button */}
      <Button
        onClick={onExport}
        disabled={isExporting}
        className="athlyn-button-primary"
      >
        <Download className="w-4 h-4 mr-2" />
        {isExporting ? 'Exporting...' : 'Export Invoices'}
      </Button>
    </div>
  );
}